import { useCallback, useRef, type KeyboardEvent, type TextareaHTMLAttributes } from 'react';

/**
 * Textarea con un toolbar minimalista de bold/italic.
 *
 * El formato es markdown subset (`**bold**`, `_italic_`) — mismas marcas
 * que entiende `renderInlineMarks()` del lado público. No metemos un
 * editor WYSIWYG full porque:
 *   - El subset cubre el 95% del caso real de descripciones de eventos.
 *   - Mantenemos el storage como string plano, sin migrations ni HTML
 *     que sanitizar del lado server.
 *   - Cero dependencias nuevas, cero peso extra en el bundle.
 *
 * UX: shortcuts Cmd/Ctrl+B y Cmd/Ctrl+I; los botones del toolbar también
 * funcionan con selección activa Y con cursor sin selección (en este
 * último caso insertan `****` / `__` y dejan el caret en el medio).
 */

interface Props extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> {
  value: string;
  onChange: (next: string) => void;
  /** Etiquetas localizadas para los botones del toolbar. */
  labels?: {
    bold?: string;
    italic?: string;
    hint?: string;
  };
}

export function RichTextEditor({ value, onChange, labels, className, ...textareaProps }: Props) {
  const ref = useRef<HTMLTextAreaElement | null>(null);

  const applyMark = useCallback(
    (mark: '**' | '_') => {
      const el = ref.current;
      if (!el) return;
      const start = el.selectionStart ?? 0;
      const end = el.selectionEnd ?? 0;
      const before = value.slice(0, start);
      const selected = value.slice(start, end);
      const after = value.slice(end);

      // Si la selección ya está envuelta por la marca, la sacamos (toggle).
      const wrapped =
        before.endsWith(mark) && after.startsWith(mark)
          ? {
              next: before.slice(0, -mark.length) + selected + after.slice(mark.length),
              unwrap: true,
            }
          : null;

      if (wrapped) {
        onChange(wrapped.next);
        requestAnimationFrame(() => {
          el.focus();
          const newStart = start - mark.length;
          el.setSelectionRange(newStart, newStart + selected.length);
        });
        return;
      }

      const inner = selected.length > 0 ? selected : '';
      const next = `${before}${mark}${inner}${mark}${after}`;
      onChange(next);
      requestAnimationFrame(() => {
        el.focus();
        const caretStart = start + mark.length;
        const caretEnd = caretStart + inner.length;
        el.setSelectionRange(caretStart, caretEnd);
      });
    },
    [value, onChange],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const key = e.key.toLowerCase();
      if (key === 'b') {
        e.preventDefault();
        applyMark('**');
      } else if (key === 'i') {
        e.preventDefault();
        applyMark('_');
      }
    },
    [applyMark],
  );

  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900 focus-within:border-neutral-600 transition">
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-neutral-800">
        <ToolbarButton
          onClick={() => applyMark('**')}
          ariaLabel={labels?.bold ?? 'Bold'}
          shortcut="⌘B"
        >
          <span className="font-bold">B</span>
        </ToolbarButton>
        <ToolbarButton
          onClick={() => applyMark('_')}
          ariaLabel={labels?.italic ?? 'Italic'}
          shortcut="⌘I"
        >
          <span className="italic font-serif">I</span>
        </ToolbarButton>
        {labels?.hint && (
          <span className="ml-auto text-[10px] text-neutral-500 hidden sm:inline">
            {labels.hint}
          </span>
        )}
      </div>
      <textarea
        {...textareaProps}
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        className={[
          'w-full bg-transparent px-4 py-3 outline-none resize-y min-h-[120px]',
          className ?? '',
        ].join(' ')}
      />
    </div>
  );
}

function ToolbarButton({
  onClick,
  ariaLabel,
  shortcut,
  children,
}: {
  onClick: () => void;
  ariaLabel: string;
  shortcut: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => {
        // Prevent stealing focus from the textarea so applyMark() can read
        // selectionStart/selectionEnd accurately on the next tick.
        e.preventDefault();
      }}
      onClick={onClick}
      aria-label={ariaLabel}
      title={`${ariaLabel} · ${shortcut}`}
      className="w-8 h-8 inline-flex items-center justify-center rounded text-neutral-300 hover:bg-neutral-800 hover:text-neutral-50 transition"
    >
      {children}
    </button>
  );
}
